<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, A11y } from 'swiper/modules';
import VipItemsStoreCardV2 from '@/components/vip/items/StoreCardV2.vue';
import VipPurchaseResult from '@/components/vip/modal/PurchaseResult.vue';
import VipStoreHistory from '@/components/vip/StoreHistory.vue';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import 'swiper/css';
import 'swiper/css/pagination';
import useSeo from '@/composables/useSeo';
import { getStandardSeoSuffixes } from '@/utils/seoConstants';

type Props = {
  // `false` = a loja NÃO é a página: ela está montada dentro de um modal (o do
  // Clube Fidelidade). Aí ela não pode escrever na URL — `?tab=`, limpar
  // `?open=` — nem assinar o title da aba, porque a rota por baixo é outra e
  // pertence a quem abriu. Na rota /vip/store fica `true` e nada muda.
  syncQuery?: boolean;
  // z-index base das janelas dela (compra, termos, resultado) quando o padrão
  // não serve — dentro do modal do hub (z-9994) os 50/60/999 originais abririam
  // ATRÁS do painel. As três se escalonam a partir deste número.
  modalZIndex?: number;
};

const { syncQuery = true, modalZIndex } = defineProps<Props>();

// Escalonadas a partir do base: compra < termos (abre de dentro da compra) <
// resultado. Sem base, os valores originais da rota.
const purchaseZIndex = computed(() => modalZIndex ?? 50);
const termsZIndex = computed(() => (modalZIndex ? modalZIndex + 1 : 60));
const resultZIndex = computed(() => (modalZIndex ? modalZIndex + 2 : 999));

const { standardTitleSuffix } = getStandardSeoSuffixes();

// Só a rota assina o title da aba (ver `syncQuery`).
if (syncQuery) {
  useSeo({
    title: `Loja ${standardTitleSuffix}`,
  });
}

const { loadUserInfosProfile } = useSmarticoUserInfos();

const { shopItems } = storeToRefs(useSmarticoShop());
const { buyShopItem, loadshopItems } = useSmarticoShop();

// Usar o composable centralizado para verificar quando Smartico está pronto
// Passamos o método específico que precisamos: getStoreItems
const { isReady, initialLoadComplete } = useSmarticoUserReady(undefined, [
  'getStoreItems',
]);

const isLoadingPage = ref(true);

const selectedItem = ref<TStoreItem>();
const openModal = ref<boolean>(false);
const isLoadingBuyItem = ref(false);
const purchaseResultOpen = ref(false);
const purchaseResultType = ref<'success' | 'error' | 'incomplete'>('success');
const purchaseResultText = ref('');
const quantity = ref(1);

type TTab = 'all' | 'ofertas' | 'points' | 'diamonds' | 'gems' | 'copa';

const COPA_CATEGORY_ID = 589;

const route = useRoute();
const router = useRouter();

// Lê o parâmetro tab da URL para inicializar a tab correta
const getInitialTab = (): TTab => {
  const tabParam = route.query.tab as string | undefined;
  const validTabs: TTab[] = [
    'all',
    'ofertas',
    'points',
    'diamonds',
    'gems',
    'copa',
  ];

  if (tabParam && validTabs.includes(tabParam as TTab)) {
    return tabParam as TTab;
  }

  return 'all';
};

const activeTab = ref<TTab>(getInitialTab());

// Função para trocar a tab e atualizar a query param na URL
function setTab(tabValue: TTab) {
  activeTab.value = tabValue;

  // Dentro do modal a tab é só estado local: a URL é da rota por baixo.
  if (!syncQuery) return;

  // Atualiza a query param na URL
  const newQuery = { ...route.query };

  if (tabValue === 'all') {
    delete newQuery.tab;
  } else {
    newQuery.tab = tabValue;
  }

  router.replace({ query: newQuery });
}

// Observa mudanças na query string para atualizar a tab (navegação externa)
watch(
  () => route.query.tab,
  (newTab) => {
    // No modal a URL não é dela: navegação externa não deve mexer na tab.
    if (!syncQuery) return;

    const validTabs: TTab[] = [
      'all',
      'ofertas',
      'points',
      'diamonds',
      'gems',
      'copa',
    ];

    if (newTab && validTabs.includes(newTab as TTab)) {
      if (activeTab.value !== newTab) {
        activeTab.value = newTab as TTab;
      }
    } else if (!newTab && activeTab.value !== 'all') {
      activeTab.value = 'all';
    }
  },
);

const showHistory = ref(false);
const historyRef = ref<any>(null);
const { isMobile } = useLayout();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

// Alterna para a visão de histórico e garante carregamento sempre (refresh)
async function openHistory() {
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch (e) {}

    return;
  }
  showHistory.value = true;
  try {
    await nextTick();
    historyRef.value?.refreshHistory();
  } catch (err) {
    console.error('Erro ao carregar histórico da loja:', err);
  }
}

function closeHistory() {
  showHistory.value = false;
}

// Ícone da moeda do item de histórico
function getCurrencyIcon(pt: any) {
  const t = String(pt || '').toLowerCase();

  if (t === 'diamonds') return 'currency-header/diamond';
  if (t === 'gems') return 'currency-header/gem';

  return 'currency-header/coin';
}

function getCurrencyLabel(pt: any) {
  const t = String(pt || '')
    .toLowerCase()
    .trim();

  if (t === 'gems') return 'GEMS';
  if (t === 'diamonds') return 'DIAMONDS';
  if (t === 'points' || t === 'coins' || t === 'coin') return 'COINS';

  if (t.includes('gem')) return 'GEMS';
  if (t.includes('diamond')) return 'DIAMONDS';

  return 'COINS';
}

const { t } = useTexts();

function getItemCurrency(item: any): TTab {
  const pt = String(item?.purchase_type || '').toLowerCase();

  if (pt === 'points' || pt === 'gems' || pt === 'diamonds') {
    return pt as TTab;
  }

  const st = String(item?.spanType || '').toLowerCase();

  if (typeof item?.price_diamonds === 'number' || st.includes('diamonds')) {
    return 'diamonds';
  }

  if (typeof item?.price_gems === 'number' || st.includes('gem')) {
    return 'gems';
  }

  return 'points';
}

const allItems = computed<TStoreItem[]>(() => shopItems.value || []);

const COMBOS_CATEGORY_ID = 488;

function hasCategory(it: TStoreItem, categoryId: number): boolean {
  return (
    Array.isArray(it?.category_ids) && it.category_ids.includes(categoryId)
  );
}

function isComboItem(it: TStoreItem): boolean {
  return hasCategory(it, COMBOS_CATEGORY_ID);
}

function isCopaItem(it: TStoreItem): boolean {
  return hasCategory(it, COPA_CATEGORY_ID);
}

const copaItems = computed<TStoreItem[]>(() => {
  return (allItems.value || []).filter((it) => isCopaItem(it));
});

const hasCopaItems = computed(() => copaItems.value.length > 0);

const storeTabs = computed(() => {
  const baseTabs = [
    { name: 'Todos', value: 'all' as TTab },
    { name: 'Ofertas', value: 'ofertas' as TTab },
    { name: 'Coins', value: 'points' as TTab },
    { name: 'Gemas', value: 'gems' as TTab },
    { name: 'Diamantes', value: 'diamonds' as TTab },
  ];

  // Antes do load terminar, mantém todas visíveis pra evitar flick
  if (isLoadingPage.value) return baseTabs;

  const tabs = baseTabs.filter((tab) => {
    if (tab.value === 'all') return true;
    if (tab.value === 'ofertas') return combosPromocionais.value.length > 0;

    return (nonComboItems.value || []).some(
      (it) => getItemCurrency(it) === tab.value,
    );
  });

  if (hasCopaItems.value) {
    tabs.splice(1, 0, { name: 'Copa', value: 'copa' as TTab });
  }

  return tabs;
});

// Comparador robusto por prioridade (asc), empurra itens sem prioridade para o final
function parsePriority(it: any): number {
  const p = it?.priority;

  if (typeof p === 'number' && Number.isFinite(p)) return p;
  if (typeof p === 'string') {
    const n = Number(p);

    if (Number.isFinite(n)) return n;
  }

  return Number.MAX_SAFE_INTEGER;
}

function byPriorityAsc(a: any, b: any): number {
  const pa = parsePriority(a);
  const pb = parsePriority(b);

  if (pa !== pb) return pa - pb;

  const ia = typeof a?.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
  const ib = typeof b?.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;

  return ia - ib;
}

const combosPromocionais = computed<TStoreItem[]>(() => {
  return (allItems.value || [])
    .filter((it) => isComboItem(it) && !isCopaItem(it))
    .sort(byPriorityAsc);
});

const nonComboItems = computed<TStoreItem[]>(() => {
  return (allItems.value || []).filter(
    (it) => !isComboItem(it) && !isCopaItem(it),
  );
});

const filteredItems = computed<TStoreItem[]>(() => {
  if (activeTab.value === 'copa') {
    return [...(copaItems.value || [])].sort(byPriorityAsc);
  }

  if (activeTab.value === 'all') {
    // Apenas itens não combo, agrupados por moeda
    return [...(nonComboItems.value || [])].sort(byPriorityAsc);
  }

  if (activeTab.value === 'ofertas') {
    // Só combos promocionais
    return combosPromocionais.value;
  }

  // Para coins, gems, diamonds: incluir combos promocionais da moeda

  const regular = (nonComboItems.value || []).filter(
    (it) => getItemCurrency(it) === activeTab.value,
  );

  const promos = (combosPromocionais.value || []).filter(
    (it) => getItemCurrency(it) === activeTab.value,
  );

  return [...regular, ...promos].sort(byPriorityAsc);
});

// Agrupamento por moeda quando a aba 'all' estiver ativa
const storeRootRef = ref<HTMLElement | null>(null);
const gridContainerRef = ref<HTMLElement | null>(null);
const tabsWrapperRef = ref<HTMLElement | null>(null);
const tabsRef = ref<HTMLElement | null>(null);
const PROMOCIONAIS_VISIBLE_COUNT = 50;
const DEFAULT_VISIBLE_COUNT = 50;
const calculatedInitialVisibleCount = ref(DEFAULT_VISIBLE_COUNT);

// Refs para grupos e contagens visíveis por moeda
const groupRefs = reactive<
  Record<'promocionais' | 'points' | 'diamonds' | 'gems', HTMLElement | null>
>({
  promocionais: null,
  points: null,
  diamonds: null,
  gems: null,
});

const visibleCounts = ref<
  Record<'promocionais' | 'points' | 'diamonds' | 'gems', number>
>({
  promocionais: 0,
  points: 0,
  diamonds: 0,
  gems: 0,
});

const calculateVisibleCount = () => {
  if (gridContainerRef.value) {
    const containerWidth = gridContainerRef.value.offsetWidth;
    const cardWidth = 208; // largura do card conforme StoreCardV2
    const gap = 12; // gap-3 = 12px

    // Calcula quantos cards cabem em uma linha
    let columns = Math.floor((containerWidth + gap) / (cardWidth + gap));

    if (columns === 0) columns = 1;

    if (activeTab.value === 'all' || activeTab.value === 'ofertas') {
      if (columns > PROMOCIONAIS_VISIBLE_COUNT) {
        columns = PROMOCIONAIS_VISIBLE_COUNT;
      }
    }
    if (
      !(activeTab.value === 'all' || activeTab.value === 'ofertas') &&
      columns > DEFAULT_VISIBLE_COUNT
    ) {
      columns = DEFAULT_VISIBLE_COUNT;
    }

    calculatedInitialVisibleCount.value = columns;
  }

  // Inicializa contadores visíveis caso ainda não tenham sido setados
  if (!visibleCounts.value.promocionais) {
    visibleCounts.value.promocionais = PROMOCIONAIS_VISIBLE_COUNT;
  }
  if (!visibleCounts.value.points) {
    visibleCounts.value.points = DEFAULT_VISIBLE_COUNT;
  }
  if (!visibleCounts.value.diamonds) {
    visibleCounts.value.diamonds = DEFAULT_VISIBLE_COUNT;
  }
  if (!visibleCounts.value.gems) {
    visibleCounts.value.gems = DEFAULT_VISIBLE_COUNT;
  }
};

// Agora cada aba mostra também as ofertas (combos promocionais) da respectiva moeda
const itemsCoins = computed<TStoreItem[]>(() => {
  const coinsRegular = (nonComboItems.value || []).filter(
    (it) => getItemCurrency(it) === 'points',
  );

  const coinsPromos = (combosPromocionais.value || []).filter(
    (it) => getItemCurrency(it) === 'points',
  );

  return [...coinsRegular, ...coinsPromos].sort(byPriorityAsc);
});

const itemsDiamonds = computed<TStoreItem[]>(() => {
  const diamondsRegular = (nonComboItems.value || []).filter(
    (it) => getItemCurrency(it) === 'diamonds',
  );

  const diamondsPromos = (combosPromocionais.value || []).filter(
    (it) => getItemCurrency(it) === 'diamonds',
  );

  return [...diamondsRegular, ...diamondsPromos].sort(byPriorityAsc);
});

const itemsGems = computed<TStoreItem[]>(() => {
  const gemsRegular = (nonComboItems.value || []).filter(
    (it) => getItemCurrency(it) === 'gems',
  );

  const gemsPromos = (combosPromocionais.value || []).filter(
    (it) => getItemCurrency(it) === 'gems',
  );

  return [...gemsRegular, ...gemsPromos].sort(byPriorityAsc);
});

const visiblePromocionais = computed<TStoreItem[]>(() => {
  return combosPromocionais.value.slice(0, PROMOCIONAIS_VISIBLE_COUNT);
});

const visibleCoins = computed<TStoreItem[]>(() => {
  return itemsCoins.value.slice(0, DEFAULT_VISIBLE_COUNT);
});

const visibleDiamonds = computed<TStoreItem[]>(() => {
  return itemsDiamonds.value.slice(0, DEFAULT_VISIBLE_COUNT);
});

const visibleGems = computed<TStoreItem[]>(() => {
  return itemsGems.value.slice(0, DEFAULT_VISIBLE_COUNT);
});

// Paginação com bolinhas para scroll horizontal
const scrollContainerRefs = reactive<Record<string, HTMLElement | null>>({
  promocionais: null,
  coins: null,
  diamonds: null,
  gems: null,
  ofertas: null,
  allPoints: null,
  allDiamonds: null,
  allGems: null,
});

const scrollPositions = ref<Record<string, number>>({
  promocionais: 0,
  coins: 0,
  diamonds: 0,
  gems: 0,
  ofertas: 0,
  allPoints: 0,
  allDiamonds: 0,
  allGems: 0,
});

const totalPages = ref<Record<string, number>>({
  promocionais: 1,
  coins: 1,
  diamonds: 1,
  gems: 1,
  ofertas: 1,
  allPoints: 1,
  allDiamonds: 1,
  allGems: 1,
});

let scrollTimeout: any = null;

// Medição de colunas por fileira nas abas
const activeGridRef = ref<HTMLElement | null>(null);
const tabsColumnsCount = ref<number>(0);
let tabsGridRO: ResizeObserver | null = null;

function recomputeTabsColumns() {
  try {
    const el = activeGridRef.value;

    if (!el || typeof window === 'undefined') return;

    const gap = 12;
    const mobile = window.innerWidth < 640;
    const cardWidth = mobile ? 160 : 208;

    let cols = Math.floor((el.clientWidth + gap) / (cardWidth + gap));

    if (cols < 1) cols = 1;
    if (cols > 5) cols = 5;

    tabsColumnsCount.value = cols;
  } catch {}
}

function attachTabsGridObserver() {
  try {
    if (tabsGridRO) {
      tabsGridRO.disconnect();
      tabsGridRO = null;
    }

    const el = activeGridRef.value;

    if (!el || typeof ResizeObserver === 'undefined') return;

    tabsGridRO = new ResizeObserver(() => recomputeTabsColumns());
    tabsGridRO.observe(el);

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => recomputeTabsColumns());
    } else {
      setTimeout(() => recomputeTabsColumns(), 0);
    }
  } catch {}
}

// Observa mudanças de tamanho/conteúdo para recalcular as páginas imediatamente
const resizeObservers = new Map<string, ResizeObserver>();

function recomputePagesForKey(key: string) {
  try {
    const container = scrollContainerRefs[key];

    if (!container) return;

    const scrollWidth = container.scrollWidth || 0;
    const clientWidth = container.clientWidth || 0;

    if (!clientWidth) {
      totalPages.value[key] = 1;

      return;
    }

    let pages = Math.ceil(scrollWidth / clientWidth);

    if (scrollWidth <= clientWidth + 1) {
      pages = 1;
    }

    // Fallback baseado em quantidade conhecida de itens (garante exibição inicial no mobile)
    const perViewMobile = 2;

    if (key === 'promocionais') {
      const count = visiblePromocionais.value?.length || 0;

      pages = Math.max(pages, Math.ceil(count / perViewMobile));
    } else if (key === 'allPoints') {
      const count = visibleCoins.value?.length || 0;

      pages = Math.max(pages, Math.ceil(count / perViewMobile));
    } else if (key === 'allDiamonds') {
      const count = visibleDiamonds.value?.length || 0;

      pages = Math.max(pages, Math.ceil(count / perViewMobile));
    } else if (key === 'allGems') {
      const count = visibleGems.value?.length || 0;

      pages = Math.max(pages, Math.ceil(count / perViewMobile));
    }

    totalPages.value[key] = Math.max(1, pages);

    // Normaliza página atual
    const maxIndex = Math.max(0, (totalPages.value[key] || 1) - 1);

    if ((scrollPositions.value[key] || 0) > maxIndex) {
      scrollPositions.value[key] = 0;
    }
  } catch {}
}

function attachResizeObserverForKey(key: string) {
  const container = scrollContainerRefs[key];

  if (!container) return;

  if (resizeObservers.has(key)) return;

  try {
    const ro = new ResizeObserver(() => {
      recomputePagesForKey(key);
    });

    ro.observe(container);
    resizeObservers.set(key, ro);

    // Computa imediatamente
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => recomputePagesForKey(key));
    } else {
      setTimeout(() => recomputePagesForKey(key), 0);
    }
  } catch {}
}

function handleScroll(key: string) {
  // Debounce para melhor performance
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }

  scrollTimeout = setTimeout(() => {
    const container = scrollContainerRefs[key];

    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    if (scrollWidth <= clientWidth) {
      scrollPositions.value[key] = 0;
      totalPages.value[key] = 1;

      return;
    }

    // Calcula a página atual baseada no scroll
    const maxScroll = scrollWidth - clientWidth;
    const progress = scrollLeft / maxScroll;
    const totalPgs = Math.max(1, Math.ceil(scrollWidth / clientWidth));

    totalPages.value[key] = totalPgs;
    scrollPositions.value[key] = Math.round(progress * (totalPgs - 1));
  }, 10);
}

// Define as páginas iniciais dos carrosséis no mobile
function computeInitialPagination() {
  try {
    const mobile = (
      isMobile && (isMobile as any).value !== undefined
        ? (isMobile as any).value
        : typeof window !== 'undefined'
          ? window.innerWidth < 768
          : true
    ) as boolean;

    if (!mobile) {
      // No desktop as bolinhas ficam ocultas (md:hidden),
      // então não precisamos calcular nada aqui.
      return;
    }

    const perViewMobile = 2;

    totalPages.value.promocionais = Math.max(
      1,
      Math.ceil((visiblePromocionais.value?.length || 0) / perViewMobile),
    );
    totalPages.value.allPoints = Math.max(
      1,
      Math.ceil((visibleCoins.value?.length || 0) / perViewMobile),
    );
    totalPages.value.allDiamonds = Math.max(
      1,
      Math.ceil((visibleDiamonds.value?.length || 0) / perViewMobile),
    );
    totalPages.value.allGems = Math.max(
      1,
      Math.ceil((visibleGems.value?.length || 0) / perViewMobile),
    );

    // Sempre começa na primeira página
    scrollPositions.value.promocionais = 0;
    scrollPositions.value.allPoints = 0;
    scrollPositions.value.allDiamonds = 0;
    scrollPositions.value.allGems = 0;
  } catch {}
}

// Função para scroll horizontal da tab ativa
function scrollActiveTabIntoView() {
  try {
    const container = tabsRef.value;

    if (!container) return;

    const activeButton = container.querySelector('.activeTab') as HTMLElement;

    if (activeButton) {
      // Calcula a posição para centralizar a tab ativa
      const scrollLeft =
        activeButton.offsetLeft -
        container.offsetWidth / 2 +
        activeButton.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  } catch {}
}

// Função para scroll da página para o topo
function scrollToTop() {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch {}
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  } catch {}
  // Scroll em containers com overflow
  try {
    let node = storeRootRef.value as HTMLElement | null;

    while (node) {
      const style = window.getComputedStyle(node);
      const overflowY = style?.overflowY;

      if (
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        try {
          if (typeof (node as any).scrollTo === 'function') {
            (node as any).scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            (node as any).scrollTop = 0;
          }
        } catch {}
      }
      node = node.parentElement as HTMLElement | null;
    }
  } catch {}
}

// Se a tab ativa sumir (categoria ficou vazia após reload), volta pra "Todos"
watch(storeTabs, (newTabs) => {
  if (!newTabs.some((tab) => tab.value === activeTab.value)) {
    setTab('all');
  }
});

// Recalcula as páginas quando muda de aba ou os itens mudam
watch(activeTab, () => {
  nextTick(() => {
    // Scroll para o topo e para a tab ativa
    scrollToTop();
    scrollActiveTabIntoView();

    // Garante que as bolinhas apareçam imediatamente no mobile
    computeInitialPagination();

    Object.keys(scrollContainerRefs).forEach((key) => {
      const container = scrollContainerRefs[key];

      if (container) {
        handleScroll(key);
      }
    });

    // Recalcula e centraliza quando houver exatamente 2 colunas por fileira
    attachTabsGridObserver();
    recomputeTabsColumns();
  });
});

watch(
  [combosPromocionais, itemsCoins, itemsDiamonds, itemsGems, filteredItems],
  () => {
    nextTick(() => {
      // Garante que as bolinhas apareçam imediatamente no mobile
      computeInitialPagination();

      Object.keys(scrollContainerRefs).forEach((key) => {
        const container = scrollContainerRefs[key];

        if (container) {
          handleScroll(key);
        }
      });

      // Recalcula e centraliza quando houver exatamente 2 colunas por fileira
      attachTabsGridObserver();
      recomputeTabsColumns();
    });
  },
);

// Quando a página terminar de carregar, calcula as páginas iniciais (mobile)
watch(isLoadingPage, (loading) => {
  if (!loading) {
    nextTick(() => {
      computeInitialPagination();

      Object.keys(scrollContainerRefs).forEach((key) => {
        const container = scrollContainerRefs[key];

        if (container) {
          handleScroll(key);
        }
      });
    });

    // Executa novamente após pequeno atraso para garantir medições pós-imagens
    setTimeout(() => {
      nextTick(() => {
        computeInitialPagination();

        Object.keys(scrollContainerRefs).forEach((key) => {
          const container = scrollContainerRefs[key];

          if (container) {
            handleScroll(key);
          }
        });
      });
    }, 150);
  }
});

onMounted(() => {
  calculateVisibleCount();
  window.addEventListener('resize', calculateVisibleCount);
  window.addEventListener('resize', recomputeTabsColumns);
  attachTabsGridObserver();

  // Inicializa os listeners de scroll e a paginação
  nextTick(() => {
    // Se há uma tab específica na URL, faz scroll para ela
    // Caso contrário, garante que o primeiro tab fique totalmente visível
    if (activeTab.value !== 'all') {
      scrollActiveTabIntoView();
    } else {
      try {
        const t = tabsRef.value as any;

        if (t) {
          if (typeof t.scrollTo === 'function') {
            t.scrollTo({ left: 0 });
          } else {
            t.scrollLeft = 0;
          }
        }
      } catch {}
    }
    Object.keys(scrollContainerRefs).forEach((key) => {
      const container = scrollContainerRefs[key];

      if (container) {
        attachResizeObserverForKey(key);
        recomputePagesForKey(key);
        handleScroll(key);
      }
    });

    // Primeira computação das colunas nas abas
    recomputeTabsColumns();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateVisibleCount);
  window.removeEventListener('resize', recomputeTabsColumns);
  try {
    if (tabsGridRO) {
      tabsGridRO.disconnect();
      tabsGridRO = null;
    }
  } catch {}

  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }

  // Desconecta observers
  try {
    resizeObservers.forEach((ro) => ro.disconnect());
    resizeObservers.clear();
  } catch {}
});

function openItemStoreDetails(item: TStoreItem) {
  // Garante que apenas usuários autenticados possam abrir o modal de compra
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch (e) {}

    return;
  }

  selectedItem.value = item;
  quantity.value = 1;
  openModal.value = true;
}

function openItemDetails(item: TStoreItem) {
  // exibe modal de login caso usuário não esteja autenticado — comportamento alinhado com missões/minigames
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch (e) {}

    return;
  }

  selectedItem.value = item;
  quantity.value = 1;
  openModal.value = true;
}

const selectedRelatedItems = computed<TStoreItem[]>(() => {
  const ids = (selectedItem.value as any)?.related_item_ids as
    | number[]
    | undefined;

  const list = Array.isArray(ids) ? ids : [];
  const map = new Map<number, TStoreItem>();

  for (const it of allItems.value || []) {
    if (typeof (it as any)?.id === 'number') map.set((it as any).id, it);
  }

  return list.map((id) => map.get(id)).filter(Boolean) as TStoreItem[];
});

function selectRelatedItem(it: TStoreItem) {
  // Selecionar item relacionado também exige autenticação (mesmo comportamento dos cartões)
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch (e) {}

    return;
  }

  selectedItem.value = it;
}

const isTermsModalVisible = ref(false);

const formattedHintText = computed(() => {
  const t = String((selectedItem.value as any)?.hint_text ?? '').trim();

  return t ? t.split('\n') : [];
});

const hasHintText = computed(() => formattedHintText.value.length > 0);

async function buyItemAction() {
  if (!selectedItem.value?.id) return;

  const safeQuantity = 1;

  isLoadingBuyItem.value = true;

  let successCount = 0;
  let lastSuccessMessage = '';
  let firstErrorMessage = '';

  try {
    const total = safeQuantity;

    for (let i = 0; i < total; i++) {
      try {
        const result = await buyShopItem(selectedItem.value.id);

        if (!result || (result?.err_code && result.err_code !== 0)) {
          const message =
            result?.err_message || t('gamification.store.fail_to_buy');

          if (!firstErrorMessage) firstErrorMessage = String(message);
        } else {
          successCount++;

          const type = selectedItem.value.type;
          const message = result?.message
            ? result.message
            : !type
              ? t('gamification.store.extended_purchase_successfully')
              : t('gamification.store.purchase_successfully');

          lastSuccessMessage = String(message);
        }
      } catch (e) {
        if (!firstErrorMessage)
          firstErrorMessage = String(t('gamification.store.fail_to_buy'));
      }
    }

    // Fecha modal de compra antes de exibir o resultado
    openModal.value = false;

    if (successCount === 0) {
      purchaseResultType.value = 'error';
      purchaseResultText.value =
        firstErrorMessage || String(t('gamification.store.fail_to_buy'));
      purchaseResultOpen.value = true;
    } else if (successCount === safeQuantity) {
      purchaseResultType.value = 'success';
      purchaseResultText.value = lastSuccessMessage;
      purchaseResultOpen.value = true;
    } else {
      purchaseResultType.value = 'incomplete';
      purchaseResultText.value = `Você conseguiu adquirir ${successCount} de ${safeQuantity} itens.`;
      purchaseResultOpen.value = true;
    }

    // atualiza histórico (tempo real no componente) quando houve ao menos 1 sucesso
    if (successCount > 0) {
      try {
        await nextTick();
        historyRef.value?.refreshHistory(); // Sempre limpa e recarrega tudo
      } catch (e) {
        console.error('Erro recarregando histórico após compra:', e);
      }
    }
  } finally {
    setTimeout(async () => {
      try {
        if (successCount > 0) {
          await loadshopItems({ force: true });
        }
        await loadUserInfosProfile();
      } finally {
        isLoadingBuyItem.value = false;
      }
    }, 80);
  }
}

function openHistoryItem(it: any) {
  const possibleId = Number(it?.store_item_id || it?.id);
  const storeByName = (allItems.value || []).find(
    (x) => x?.name === it?.name,
  ) as TStoreItem | undefined;

  const targetId = Number.isFinite(possibleId) ? possibleId : storeByName?.id;

  const found = (allItems.value || []).find((x) => x?.id === targetId) as
    | TStoreItem
    | undefined;

  if (found) {
    openItemStoreDetails(found);

    return;
  }

  // Fallback: garante purchase_type correto e descrição
  const derivedCurrency = getItemCurrency(it as any); // 'points' | 'diamonds' | 'gems'
  const fallback = {
    id: targetId,
    name: it?.name,
    image: it?.image || storeByName?.image,
    description: storeByName?.description ?? it?.description ?? '',
    price: it?.purchase_points_amount ?? it?.price,
    purchase_type: derivedCurrency,
    purchase_points_amount: it?.purchase_points_amount,
    can_buy: true,
  } as unknown as TStoreItem;

  openItemStoreDetails(fallback);
}

// Função simplificada para carregar a loja
// O composable useSmarticoUserReady já garante que a API está pronta
const triggerLoadStore = async () => {
  isLoadingPage.value = true;

  try {
    await loadshopItems({ force: true });
  } catch (err) {
    console.warn('Erro ao carregar itens da loja:', err);
  } finally {
    setTimeout(() => {
      isLoadingPage.value = false;
    }, 300);
  }
};

// Aguardar Smartico estar pronto antes de carregar a loja
watch(
  isReady,
  async (ready) => {
    if (ready) {
      await triggerLoadStore();
      initialLoadComplete.value = true;
    }
  },
  { immediate: true },
);

// Deep link via ?open=<id> — abre o item da loja automaticamente
function clearOpenQueryParam() {
  const newQuery = { ...route.query };

  delete newQuery.open;
  router.replace({ query: newQuery });
}

function tryOpenStoreDeepLink() {
  // Deep-link é coisa da rota. No modal esse `?open=` é de OUTRO dono — o
  // próprio hub usa `?open=clube-fidelidade` — e o `clearOpenQueryParam` abaixo
  // apagaria a query de quem está por baixo.
  if (!syncQuery) return;

  const openParam = route.query.open as string | undefined;

  if (!openParam) return;
  if (isLoadingPage.value) return;

  const targetId = Number(openParam);

  clearOpenQueryParam();

  if (!Number.isFinite(targetId)) return;

  const item = (allItems.value || []).find(
    (it) => Number((it as any)?.id) === targetId,
  );

  if (item) {
    openItemDetails(item);
  }
}

watch([() => route.query.open, isLoadingPage], () => tryOpenStoreDeepLink(), {
  immediate: true,
});
</script>

<template>
  <div
    ref="storeRootRef"
    class="storeV2"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :hide-back="!syncQuery"
      title="Loja"
    >
      <template #right>
        <button
          v-if="!showHistory"
          aria-label="Atualizar loja"
          class="actionButton"
          @click="triggerLoadStore"
        >
          <NuxtIcon
            aria-hidden="true"
            :class="{ 'animate-spin': isLoadingPage }"
            name="sync"
          />
        </button>
        <button
          :aria-label="showHistory ? 'Ver loja' : 'Ver histórico'"
          class="actionButton historyBtn md:!hidden"
          @click="showHistory ? closeHistory() : openHistory()"
        >
          <NuxtIcon
            aria-hidden="true"
            :name="showHistory ? 'store/store' : 'mini-games/historico'"
          />
          <span class="historyText">
            {{ showHistory ? 'LOJA' : 'HISTÓRICO' }}
          </span>
        </button>
      </template>
    </BaseScreenHeader>

    <!-- Container de conteúdo -->
    <div class="store-content">
      <!-- Abas de filtro -->
      <section class="section">
        <div
          ref="tabsWrapperRef"
          class="tabs-wrapper relative"
        >
          <!-- Indicador visual para scroll lateral no mobile -->

          <div
            ref="tabsRef"
            class="tabs"
          >
            <BaseFilterTabs
              :model-value="activeTab"
              :tabs="storeTabs"
              @update:model-value="setTab($event as TTab)"
            />
          </div>
          <button
            class="history-button hidden md:!flex h-[41px] w-auto justify-center items-center gap-[15px] border [background:#010612] backdrop-blur-[10px] px-4 py-2 rounded-[10px] border-solid border-[#1D2329] text-white"
            @click="showHistory ? closeHistory() : openHistory()"
          >
            <NuxtIcon
              class="text-[20px] text-white"
              filled
              :name="showHistory ? 'store/store' : 'mini-games/historico'"
            />
            <span class="text-white text-[13px] font-bold leading-4">
              {{ showHistory ? 'VER LOJA' : 'VER HISTÓRICO' }}
            </span>
          </button>
        </div>

        <!-- Histórico de compras -->
        <div v-if="showHistory">
          <VipStoreHistory
            ref="historyRef"
            :active-tab="activeTab"
            :expand-all-by-default="true"
            @open-item="openHistoryItem"
          />
        </div>

        <!-- Skeleton da loja -->
        <template v-else-if="isLoadingPage">
          <!-- Tab "Todos" usa skeleton com seções de scroll horizontal -->
          <SmarticoApostatudoSkeletonStoreGroupsSkeleton
            v-if="activeTab === 'all'"
            :card-height="270"
            :card-width="208"
            :cards-per-group="5"
            :groups="4"
          />
          <!-- Outras tabs usam skeleton de grid -->
          <SmarticoApostatudoSkeletonStoreSkeleton
            v-else
            :count="8"
          />
        </template>

        <template v-else>
          <!-- Quando estiver em 'TODOS' (all): separar por moeda (Coins, Diamonds, Gems) -->
          <div
            v-if="activeTab === 'all'"
            ref="gridContainerRef"
          >
            <!-- PROMOCIONAIS -->
            <div
              v-if="combosPromocionais.length > 0"
              :ref="(el) => (groupRefs.promocionais = el as HTMLElement)"
              class="mb-6"
            >
              <PerfilOficialSwiper
                icon-path="home/ganhos-turbo"
                :see-more-url="'/vip/store' + '?tab=ofertas'"
                title="Promocionais"
              >
                <VipItemsStoreCardV2
                  v-for="item in visiblePromocionais"
                  :key="`item-${item.id}`"
                  :force-name-font16="true"
                  :item="item"
                  @open-details="openItemDetails"
                  @select-item="openItemStoreDetails"
                />
              </PerfilOficialSwiper>
            </div>

            <!-- COINS -->
            <div
              v-if="itemsCoins.length > 0"
              :ref="(el) => (groupRefs.points = el as HTMLElement)"
              class="mb-6"
            >
              <PerfilOficialSwiper
                icon-path="store/coin"
                :see-more-url="'/vip/store' + '?tab=points'"
                title="Coins"
              >
                <VipItemsStoreCardV2
                  v-for="item in visibleCoins"
                  :key="`item-${item.id}`"
                  :item="item"
                  @open-details="openItemDetails"
                  @select-item="openItemStoreDetails"
                />
              </PerfilOficialSwiper>
            </div>

            <!-- DIAMONDS -->
            <div
              v-if="itemsDiamonds.length > 0"
              :ref="(el) => (groupRefs.diamonds = el as HTMLElement)"
              class="mb-6"
            >
              <PerfilOficialSwiper
                icon-path="store/diamond"
                :see-more-url="'/vip/store' + '?tab=diamonds'"
                title="Diamonds"
              >
                <VipItemsStoreCardV2
                  v-for="item in visibleDiamonds"
                  :key="`item-${item.id}`"
                  :item="item"
                  @open-details="openItemDetails"
                  @select-item="openItemStoreDetails"
                />
              </PerfilOficialSwiper>
            </div>

            <!-- GEMS -->
            <div
              v-if="itemsGems.length > 0"
              :ref="(el) => (groupRefs.gems = el as HTMLElement)"
              class="mb-6"
            >
              <PerfilOficialSwiper
                icon-path="store/gem"
                :see-more-url="'/vip/store' + '?tab=gems'"
                title="Gems"
              >
                <VipItemsStoreCardV2
                  v-for="item in visibleGems"
                  :key="`item-${item.id}`"
                  :item="item"
                  @open-details="openItemDetails"
                  @select-item="openItemStoreDetails"
                />
              </PerfilOficialSwiper>
            </div>

            <div
              v-if="
                combosPromocionais.length === 0 &&
                itemsCoins.length === 0 &&
                itemsDiamonds.length === 0 &&
                itemsGems.length === 0
              "
              class="alert alert-warning"
            >
              <span>Nenhum item disponível nesta categoria.</span>
            </div>
          </div>

          <!-- Demais abas continuam usando filtragem padrão -->
          <div v-else>
            <div
              v-if="filteredItems.length > 0"
              :ref="
                (el) => {
                  scrollContainerRefs[activeTab] = el as HTMLElement;
                  activeGridRef = el as unknown as HTMLElement | null;
                }
              "
              class="gridItems"
              :class="{
                'single-item': filteredItems.length === 1,
                'two-items': filteredItems.length === 2,
                'two-columns': tabsColumnsCount === 2,
              }"
              @scroll="handleScroll(activeTab)"
            >
              <VipItemsStoreCardV2
                v-for="item in filteredItems"
                :key="`item-${item.id}`"
                :item="item"
                @open-details="openItemDetails"
                @select-item="openItemStoreDetails"
              />
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>

  <!-- Modal de compra (janela de compra) -->
  <BaseModalMissionModal
    v-model="openModal"
    :z-index="purchaseZIndex"
  >
    <template #header>
      <NuxtIcon
        class="text-xl text-white"
        :filled="true"
        name="store/cart"
      />
      <p class="text-lg font-bold text-white">Resumo da compra</p>
    </template>
    <div
      v-if="selectedItem"
      class="w-full inline-flex flex-col justify-start items-start gap-3.5"
    >
      <BaseLoading
        class="!bg-auth-bg-primary"
        :show="isLoadingBuyItem"
      />

      <!-- Conteúdo do modal -->
      <div
        class="self-stretch flex flex-col justify-center items-start gap-2.5 overflow-hidden"
      >
        <!-- Bloco: item + quantidade -->
        <div
          class="self-stretch flex flex-col justify-start items-start gap-2.5"
        >
          <div
            class="self-stretch p-3.5 sm:p-5 bg-gray-950 rounded-[10px] border-[1.5px] border-solid border-white/10 backdrop-blur-[10px] flex flex-col sm:flex-row justify-center sm:justify-between items-start sm:items-center gap-2.5 sm:gap-4"
          >
            <!-- Bloco superior: imagem + textos -->
            <div
              class="self-stretch sm:flex-1 rounded-[10px] inline-flex justify-center items-center gap-2.5"
            >
              <img
                :alt="selectedItem.name"
                class="w-14 h-14 rounded-[6px] object-cover flex-shrink-0"
                :src="selectedItem.image"
              />
              <div
                class="flex-1 inline-flex flex-col justify-start items-start gap-0.5 min-w-0"
              >
                <div
                  class="self-stretch justify-center text-white font-semibold leading-5 line-clamp-2"
                  style="font-size: 15px"
                  :title="selectedItem.name"
                >
                  {{ selectedItem.name }}
                </div>
                <div
                  class="self-stretch justify-center text-white/50 text-sm font-medium leading-4 line-clamp-3"
                >
                  {{ selectedItem.description }}
                </div>
                <div
                  v-if="hasHintText"
                  class="px-2.5 mt-1 py-1 bg-gray-950 rounded-[10px] border-[1.5px] border-solid border-white/10 backdrop-blur-[10px] inline-flex justify-start items-center gap-[5px]"
                  @click="isTermsModalVisible = true"
                >
                  <div
                    class="justify-center text-white/50 text-xs font-medium leading-3 cursor-pointer"
                  >
                    Termos e condições
                  </div>
                  <NuxtIcon
                    class="text-[12px] text-white/50 cursor-pointer"
                    name="info"
                  />
                </div>

                <BaseModalMissionModal
                  v-model="isTermsModalVisible"
                  :z-index="termsZIndex"
                >
                  <template #header>
                    <NuxtIcon
                      class="text-xl text-white"
                      name="mission/info"
                    />
                    <p class="text-lg font-bold text-white"
                      >Termos e condições</p
                    >
                  </template>
                  <div v-if="hasHintText">
                    <p
                      v-for="(line, index) in formattedHintText"
                      :key="index"
                      class="mb-2 text-white/50 font-normal text-[13px]"
                    >
                      {{ line }}
                    </p>
                  </div>
                </BaseModalMissionModal>
              </div>
            </div>

            <!-- Bloco inferior: seletor de quantidade -->
            <!-- <div
              class="self-stretch sm:self-auto rounded-[10px] flex flex-col justify-center items-center gap-2.5"
            >
              <div
                class="w-28 text-center justify-center text-white/50 text-[10px] font-semibold leading-4"
              >
                QUANTIDADE
              </div>

              <div
                aria-label="Seletor de quantidade"
                class="self-stretch sm:w-28 h-9 px-3.5 py-2.5 bg-white/5 rounded-[10px] inline-flex justify-center items-center select-none gap-7"
              >
                <button
                  aria-label="Diminuir"
                  class="text-white/50 text-lg font-semibold hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="quantity <= MIN_QTY || !selectedItem.can_buy"
                  type="button"
                  @click="decQty"
                  >−</button
                >

                <div
                  class="text-center text-white text-base font-semibold tabular-nums"
                >
                  {{ quantity }}
                </div>

                <button
                  aria-label="Aumentar"
                  class="text-white/50 text-lg font-semibold hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="quantity >= MAX_QTY || !selectedItem.can_buy"
                  type="button"
                  @click="incQty"
                  >+</button
                >
              </div>
            </div> -->
          </div>
        </div>

        <!-- Custo total -->
        <div
          class="self-stretch py-2.5 bg-gray-950 rounded-[10px] backdrop-blur-[10px] inline-flex flex-row justify-center items-center gap-2 sm:gap-3.5"
        >
          <!-- <div
            class="sm:w-32 sm:text-right text-white text-base font-semibold leading-none whitespace-nowrap"
          >
            CUSTO TOTAL:
          </div> -->
          <NuxtIcon
            class="text-[28px] flex-shrink-0"
            :filled="true"
            :name="getCurrencyIcon(selectedItem?.purchase_type)"
          />
          <div
            class="text-center justify-start text-[#00c9ff] text-base font-extrabold leading-tight"
          >
            {{
              (
                (selectedItem.purchase_points_amount ?? selectedItem.price) ||
                0
              ).toLocaleString('pt-BR')
            }}
            {{ getCurrencyLabel(selectedItem?.purchase_type) }}
          </div>
        </div>

        <!-- Botão concluir -->
        <button
          class="self-stretch h-12 px-2.5 rounded-md outline outline-1 outline-offset-[-1px] outline-red-300 inline-flex justify-center items-center gap-1.5 [background:radial-gradient(ellipse_138%_168.3%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] hover:brightness-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="!selectedItem.can_buy || isLoadingBuyItem"
          @click="buyItemAction"
        >
          <span class="text-white text-base font-semibold leading-3">
            CONCLUIR COMPRA
          </span>
          <NuxtIcon
            class="text-white text-[18px]"
            :filled="true"
            name="store/cart"
          />
        </button>

        <!-- Limite / alerta (quando aplicável) -->
        <div
          v-if="!selectedItem.can_buy"
          class="self-stretch mt-1"
        >
          <div class="alert alert-warning">
            {{ selectedItem.limit_message }}
          </div>
        </div>

        <!-- Itens relacionados -->
        <div class="self-stretch mt-3">
          <ClientOnly v-if="selectedRelatedItems.length > 0">
            <div
              class="flex justify-center items-center text-white/50 text-sm mb-[10px] text-[10px]"
            >
              ITENS RELACIONADOS
            </div>
            <Swiper
              :breakpoints="{
                640: { spaceBetween: 10 },
                1024: { spaceBetween: 10 },
              }"
              :center-insufficient-slides="true"
              class="related-swiper"
              direction="horizontal"
              :modules="[Pagination, A11y]"
              :pagination="{
                el: '.swiper-pagination-related',
                clickable: true,
              }"
              :slides-per-view="'auto'"
              :space-between="10"
            >
              <SwiperSlide
                v-for="rel in selectedRelatedItems"
                :key="String((rel as any)?.id)"
              >
                <div
                  class="w-[92px] p-2.5 bg-gray-950 rounded-[10px] border-[1.5px] border-solid border-white/10 backdrop-blur-[10px] inline-flex flex-col justify-start items-center gap-[5px] cursor-pointer hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white"
                  role="button"
                  tabindex="0"
                  @click="selectRelatedItem(rel)"
                  @keyup.enter="selectRelatedItem(rel)"
                >
                  <img
                    :alt="(rel as any)?.name"
                    class="w-10 h-10 object-contain"
                    :src="(rel as any)?.image"
                  />
                  <div
                    class="w-full text-center text-white/80 text-[10px] font-bold leading-[100%] tracking-[0%] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden"
                    :title="(rel as any)?.name"
                  >
                    {{ (rel as any)?.name }}
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            <template #fallback>
              <div class="overflow-x-auto pb-1">
                <div class="w-max mx-auto inline-flex gap-[10px]">
                  <div
                    v-for="rel in selectedRelatedItems"
                    :key="`fallback-${String((rel as any)?.id)}`"
                    class="w-[92px] p-2.5 bg-gray-950 rounded-[10px] border-[1.5px] border-solid border-white/10 backdrop-blur-[10px] inline-flex flex-col justify-center items-center gap-[5px] cursor-pointer hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white flex-shrink-0"
                    role="button"
                    tabindex="0"
                    @click="selectRelatedItem(rel)"
                    @keyup.enter="selectRelatedItem(rel)"
                  >
                    <img
                      :alt="(rel as any)?.name"
                      class="w-10 h-10 object-contain"
                      :src="(rel as any)?.image"
                    />
                    <div
                      class="w-full text-center text-white text-[10px] font-bold leading-[100%] tracking-[0%] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden"
                      :title="(rel as any)?.name"
                    >
                      {{ (rel as any)?.name }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div
              v-if="selectedRelatedItems.length > 1"
              class="swiper-pagination swiper-pagination-related mt-3"
            ></div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </BaseModalMissionModal>
  <Teleport to="body">
    <div
      v-if="purchaseResultOpen"
      class="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      :style="{ zIndex: resultZIndex }"
      @click.self="purchaseResultOpen = false"
      @keydown.esc="purchaseResultOpen = false"
    >
      <transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-2 scale-95"
      >
        <div
          v-if="purchaseResultOpen"
          class="relative"
        >
          <VipPurchaseResult
            :error-text="purchaseResultText"
            :incomplete-text="purchaseResultText"
            :show-close="true"
            :success-text="purchaseResultText"
            :type="purchaseResultType"
            @close="purchaseResultOpen = false"
          />
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<style scoped lang="postcss">
.storeV2 {
  @apply w-full min-h-[560px] mb-10 -mt-3 lg:-mt-1;
}

.actionButton {
  @apply p-2 bg-transparent text-white/60 hover:text-white transition-colors cursor-pointer flex items-center justify-center;

  svg {
    @apply w-5 h-5;
  }
}

.historyBtn {
  @apply flex items-center gap-1;
}

.historyText {
  @apply text-[10px] font-semibold text-white;
}

/* Container de conteúdo */
.store-content {
  @apply w-full;
}

.section {
  @apply mb-6;

  /* No mobile, remove margens extras */
  @media (max-width: 767px) {
    @apply mb-4 mt-0 pt-0;
  }
}

.gridFeatured {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-stretch content-stretch;
}

.gridItems {
  @apply flex gap-3 overflow-x-auto pb-2;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Mobile: grid vertical de 2 colunas nas abas específicas (OFERTAS, COINS, DIAMANTES, GEMAS) */
@media (max-width: 639px) {
  .gridItems {
    @apply grid overflow-x-visible;
    grid-template-columns: repeat(2, 1fr);
    scroll-snap-type: none;
    gap: 12px;
  }

  .gridItems > * {
    flex: unset;
    scroll-snap-align: unset;
  }
}

/* Tablet: mostra 3-4 cards */
@media (min-width: 640px) and (max-width: 1023px) {
  .gridItems {
    @apply grid overflow-x-visible;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    scroll-snap-type: none;
  }

  .gridItems > * {
    flex: unset;
  }
}

/* Tablet: centralizar itens quando só existem 2 itens */
@media (min-width: 640px) and (max-width: 1023px) {
  .gridItems.two-items,
  .gridItems.two-columns {
    grid-template-columns: repeat(2, 208px);
    justify-content: center;
    gap: 12px;
  }
}

/* Desktop: mostra 5 cards por fileira e múltiplas linhas (tabs mostram tudo) */
@media (min-width: 1024px) {
  .gridItems {
    @apply grid overflow-x-visible;
    grid-template-columns: repeat(auto-fill, minmax(208px, 1fr));
    scroll-snap-type: none;
  }

  .gridItems > * {
    flex: unset;
  }
}

.gridItems.single-item {
  grid-template-columns: 208px;
  justify-content: center;
}

.gridItems.single-item > * {
  max-width: none !important;
}

/* (removido: mantemos apenas a regra centralizada para 2 itens no tablet) */

/* No desktop, grid padrão para todos os casos; sem centralização especial para 2 itens */

/* Grid com máximo de 5 itens e scroll horizontal */
.gridItemsWithScroll {
  @apply flex gap-3 overflow-x-auto pb-2;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none; /* IE/Edge - esconder barra por padrão */
  scrollbar-width: none; /* Firefox - esconder barra por padrão */
}

/* Mobile: mostra 2 cards (160px cada) + pedaço do terceiro */
@media (max-width: 639px) {
  .gridItemsWithScroll > * {
    flex: 0 0 160px;
    scroll-snap-align: start;
  }
}

/* Mobile: centralizar itens quando só existem 2 itens */
@media (max-width: 639px) {
  .gridItems.two-items,
  .gridItems.two-columns {
    grid-template-columns: repeat(2, 160px);
    justify-content: center;
    gap: 12px;
  }
}

/* Tablet: mostra 3-4 cards */
@media (min-width: 640px) and (max-width: 1023px) {
  .gridItems.two-items,
  .gridItems.two-columns {
    grid-template-columns: repeat(2, 208px);
    justify-content: center;
    gap: 12px;
  }
}

/* Desktop: uma única fileira; scroll horizontal apenas se necessário */
@media (min-width: 1024px) {
  .gridItemsWithScroll {
    @apply flex overflow-x-auto;
    scroll-snap-type: x mandatory;
    gap: 12px; /* consistente com gap-3 */
    padding-right: 12px; /* evita cortar o último card */
  }

  .gridItemsWithScroll > * {
    flex: 0 0 208px; /* largura real do card no desktop */
    scroll-snap-align: start;
  }
}

.gridItemsWithScroll::-webkit-scrollbar {
  height: 0px; /* WebKit - esconder barra */
  display: none;
}

.gridItems::-webkit-scrollbar {
  height: 0px;
  display: none;
}

.tabs-wrapper {
  @apply flex justify-between items-center mb-4 border-b border-[#424344] gap-[5px];
  position: relative;

  /* No mobile, remove margem superior extra */
  @media (max-width: 767px) {
    @apply mt-0;
  }
}

.tabs {
  @apply flex justify-center overflow-x-auto flex-1;
  gap: 7.5px;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  /* No desktop, alinha à esquerda */
  @media (min-width: 1024px) {
    @apply justify-start;
  }
}

/* Indicadores visuais para scroll lateral no mobile */

.history-button {
  @apply flex-shrink-0;
}

@media (max-width: 639px) {
  /* Removido padding lateral para evitar manchas no mobile */
  .tabs-wrapper {
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 18px;
  }
}

/* Tablet: centralizar tabs mantendo botão de histórico à direita */
@media (min-width: 640px) and (max-width: 1023px) {
  .tabs {
    margin-left: 0;
    margin-right: auto;
    flex: 0 1 auto; /* evita ocupar 100% e permite centralizar com auto margins */
  }
}

.toggleButton {
  @apply flex w-full h-[41px] justify-center items-center gap-[15px] border [background:#010612] backdrop-blur-[10px] p-5 rounded-[10px] border-solid border-[#1D2329] text-white;
}
/* Transição para o card de resultado (fade + scale) */
.fade-scale-enter-active,
.fade-scale-leave-active {
  @apply transition duration-200 ease-out;
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  @apply opacity-0 scale-95;
}
.fade-scale-enter-to,
.fade-scale-leave-from {
  @apply opacity-100 scale-100;
}
/* Bullets do Swiper para 'Itens relacionados' */
:deep(.swiper-pagination-related) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: static !important;
  width: auto;
  left: auto;
  bottom: auto;
}

:deep(.swiper-pagination-related .swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  opacity: 1;
  transition: all 0.2s ease;
}

:deep(.swiper-pagination-related .swiper-pagination-bullet-active) {
  width: 24px;
  background: #ffffff;
}

:deep(.related-swiper .swiper-wrapper) {
  flex-direction: row !important; /* FORÇA horizontal para itens relacionados */
}

:deep(.related-swiper .swiper-slide) {
  width: 92px !important; /* fixa a largura do slide para 92px (Figma) */
  flex-shrink: 0 !important; /* impede compressão */
}

/* Center related items when slides don't overflow */
:deep(.related-swiper.swiper-center-insufficient-slides .swiper-wrapper) {
  justify-content: center;
}

/* Desktop: evita corte do último card relacionado */
@media (min-width: 1024px) {
  :deep(.related-swiper) {
    padding-right: 12px;
    overflow: visible;
  }

  :deep(.related-swiper .swiper-wrapper) {
    padding-right: 12px;
    overflow: visible !important;
  }
}
</style>
